import { useMemo } from 'react'
import { runInAction, toJS } from 'mobx'
import { omit } from 'lodash'
import { format, parse } from 'date-fns'
import { z } from 'zod'
import type { TextInputValue } from 'suomifi-ui-components'
import { post } from 'common/src/vtj/browser/http'
import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import type { UaetUiForm } from 'todistukset-common/src/vtj/types/form-types.type'
import { refreshSession } from 'todistukset-ui/src/vtj/stores/actions/session-timer-actions'
import RequestStatusEnum from 'todistukset-ui/src/vtj/RequestStatus.enum'
import {
  type Etunimet,
  type Kansalaisuus,
  type Siviilisaaty,
  type Syntymapaikka,
  type Syntymavaltio,
  type Syntymapaiva,
  editVtjArvo,
  editIlmoitettuArvo,
  finalEditableFieldValue,
  IncludeApostille,
  IncludeKaannos,
  KaannosKieli,
} from 'todistukset-common/src/vtj/types/editable-field.type'
import {
  isCountryKoodistoCode,
  type CountryKoodistoCode,
} from 'todistukset-common/src/vtj/vtj-koodisto.util'
import { MaritalStatus } from 'todistukset-common/src/vtj/MaritalStatus.enum'
import { isValueInEnum } from 'todistukset-common/src/vtj/type-guards'
import { selectBirthPlaceByLang } from 'todistukset-common/src/vtj/birth-place.util'
import {
  SFI_DATE_INPUT_FORMAT,
  DATE_FORMAT as DATE_STORE_FORMAT,
} from 'todistukset-common/src/vtj/util/date.util'
import {
  availableUAETTodistusLanguages,
  availableTranslations,
} from 'todistukset-common/src/vtj/language.util'
import { mailDeliveryFields } from 'todistukset-common/src/vtj/mail-delivery.util'
import {
  handleError,
  setOrderRequestStatus,
} from 'todistukset-ui/src/vtj/stores/actions/common-actions'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import { useUaetForm } from 'todistukset-ui/src/vtj/stores/uaet-form.store'
import { CreateOrderV2Response } from 'todistukset-ui/src/vtj/stores/actions/get-form-actions'

type Form = UaetUiForm
type Setters = typeof setters
export type UaetFormActions = {
  [Prop in keyof Setters]: Setters[Prop] extends (
    form: Form,
    ...args: infer Args
  ) => void
    ? (...args: Args) => void
    : never
}

type SingleSelectValue<T> = { uniqueItemId: T } | null
type MultiSelectValue<T> = { uniqueItemId: T }[]
type CheckboxValue = { checkboxState: boolean }

export const useSubmitUaetOrdererForm: () => () => void = () => {
  const { order } = useStore()
  const form = useUaetForm()

  return async (): Promise<void> => {
    if (order.requestStatus === RequestStatusEnum.SENDING) {
      return
    }

    setOrderRequestStatus({ order, requestStatus: RequestStatusEnum.SENDING })

    try {
      const formData = toJS(form)
      if (!formData.spouseA.birthPlaceVTJ) {
        throw new Error('typecheck error')
      }
      const apiData = {
        ...formData,
        spouseA: {
          ...omit(formData.spouseA, 'birthPlaceVTJ'),
          birthPlace: editVtjArvo<Syntymapaikka>(
            null,
            selectBirthPlaceByLang(
              formData.spouseA.birthPlaceVTJ,
              finalEditableFieldValue(formData.todistusLanguage) ??
                LanguageCode.FI
            )
          ),
        },
      }
      const [error, response] = await post<CreateOrderV2Response>(
        '/api/restricted/todistukset/uaet/new',
        apiData
      )

      if (error) {
        throw error
      }

      setOrderRequestStatus({ order, requestStatus: RequestStatusEnum.SENT })
      response.data.isCertificateFreeOfCharge
        ? window.location.replace(response.data.orderCompleteHref)
        : window.location.replace(response.data.paytrailHref)
    } catch (error) {
      const storePath = 'order.requestStatus'
      const errorValue = RequestStatusEnum.ERROR
      handleError({ error, storePath, errorValue })
    }
  }
}

export function useUaetFormActions(): UaetFormActions {
  const form = useUaetForm()
  return useMemo(() => getUaetFormActions(form), [form])
}

export function getUaetFormActions(form: Form): UaetFormActions {
  return (Object.keys(setters) as (keyof Setters)[]).reduce(
    (actions, fnName) => {
      actions[fnName] = (...args: unknown[]) => {
        runInAction(() => {
          refreshSession()
          const setter = setters[fnName] as (
            form: Form,
            ...args: unknown[]
          ) => void
          setter(form, ...args)
        })
      }
      return actions
    },
    {} as UaetFormActions
  )
}

const setters = {
  setSpouseASukulaisuussuhdeVakuutettu,
  setSpouseAMaritalStatus,
  setSpouseBFirstNames,
  setSpouseBLastName,
  setSpouseBBirthdate,
  setSpouseBCitizenshipCode,
  setSpouseBBirthPlace,
  setSpouseBBirthCountryCode,
  setSpouseBMaritalStatus,
  setPlaceOfMarriage,
  setTodistusLanguage,
  setIncludeTranslationForm,
  setTranslationFormLanguage,
  setIncludeApostille,
  setContactPhone,
  setContactEmail,
  setMailDeliveryFirstName: getMailDeliveryFieldSetter('firstName'),
  setMailDeliveryLastName: getMailDeliveryFieldSetter('lastName'),
  setMailDeliveryStreetAddress: getMailDeliveryFieldSetter('streetAddress'),
  setMailDeliveryStreetAddressExtras: getMailDeliveryFieldSetter(
    'streetAddressExtras'
  ),
  setMailDeliveryPostalCode: getMailDeliveryFieldSetter('postalCode'),
  setMailDeliveryPostOffice: getMailDeliveryFieldSetter('postOffice'),
  setMailDeliveryCountry: getMailDeliveryFieldSetter('country'),
}

function setSpouseASukulaisuussuhdeVakuutettu(
  form: Form,
  value: CheckboxValue
) {
  form.spouseA.sukulaisuussuhdeVakuutettu = editIlmoitettuArvo(
    form.spouseA.sukulaisuussuhdeVakuutettu,
    value.checkboxState
  )
}

function setSpouseBFirstNames(form: Form, value: TextInputValue) {
  form.spouseB.firstNames = editIlmoitettuArvo<Etunimet>(
    null,
    typeof value === 'string' ? value : null
  )
}

function setSpouseBLastName(form: Form, value: TextInputValue) {
  form.spouseB.lastName = editIlmoitettuArvo<Etunimet>(
    null,
    typeof value === 'string' ? value : null
  )
}

function setSpouseBBirthdate(form: Form, { value }: { value: string }) {
  try {
    const parsedDate = parse(value ?? '', SFI_DATE_INPUT_FORMAT, new Date())
    const storeFormat = format(parsedDate, DATE_STORE_FORMAT)
    // Comparison to minimum year is to prevent filling the field as user has typed
    // from '1.1.2' to '1.1.0002'
    form.spouseB.birthdate = editIlmoitettuArvo<Syntymapaiva>(
      null,
      parsedDate.getFullYear() >= 1900 ? storeFormat : value
    )
  } catch (e) {
    form.spouseB.birthdate = editIlmoitettuArvo<Syntymapaiva>(null, value)
  }
}

function setSpouseBCitizenshipCode(
  form: Form,
  value: MultiSelectValue<CountryKoodistoCode>
) {
  form.spouseB.citizenshipCode = editIlmoitettuArvo<Kansalaisuus>(
    null,
    Array.isArray(value)
      ? (Array.isArray(value) ? value : []).map(
          ({ uniqueItemId }) => uniqueItemId
        )
      : []
  )
}

function setSpouseBBirthPlace(form: Form, value: TextInputValue) {
  form.spouseB.birthPlace = editIlmoitettuArvo<Syntymapaikka>(
    null,
    typeof value === 'string' ? value : null
  )
}

function setSpouseBBirthCountryCode(
  form: Form,
  value: SingleSelectValue<CountryKoodistoCode>
) {
  form.spouseB.birthCountryCode = editIlmoitettuArvo<Syntymavaltio>(
    null,
    typeof value?.uniqueItemId === 'string' ? value.uniqueItemId : null
  )
}

function setSpouseAMaritalStatus(
  form: Form,
  value: SingleSelectValue<MaritalStatus | null>
) {
  form.spouseA.maritalStatus = editIlmoitettuArvo<Siviilisaaty>(
    form.spouseA.maritalStatus,
    isValueInEnum(value?.uniqueItemId, MaritalStatus)
      ? value.uniqueItemId
      : null
  )
}

function setSpouseBMaritalStatus(
  form: Form,
  value: SingleSelectValue<MaritalStatus | null>
) {
  form.spouseB.maritalStatus = editIlmoitettuArvo<Siviilisaaty>(
    null,
    isValueInEnum(value?.uniqueItemId, MaritalStatus)
      ? value.uniqueItemId
      : null
  )
}

function setPlaceOfMarriage(
  form: Form,
  value: SingleSelectValue<CountryKoodistoCode>
) {
  const newPlaceOfMarriage = isCountryKoodistoCode(value?.uniqueItemId)
    ? value.uniqueItemId
    : null
  if (finalEditableFieldValue(form.placeOfMarriage) !== newPlaceOfMarriage) {
    form.includeApostille = editIlmoitettuArvo<IncludeApostille>(null, false)
    form.includeTranslationForm = editIlmoitettuArvo<IncludeKaannos>(
      null,
      false
    )
    form.translationFormLanguage = editIlmoitettuArvo<KaannosKieli>(null, null)
  }
  form.placeOfMarriage = editIlmoitettuArvo(
    form.placeOfMarriage,
    newPlaceOfMarriage
  )
}

function setTodistusLanguage(form: Form, value: TextInputValue) {
  const selectedTodistusLanguage = isValueInEnum(
    value,
    z.enum(availableUAETTodistusLanguages).Enum
  )
    ? value
    : null
  form.todistusLanguage = editIlmoitettuArvo(
    form.todistusLanguage,
    selectedTodistusLanguage
  )
  if (selectedTodistusLanguage === LanguageCode.EN) {
    form.includeTranslationForm = editIlmoitettuArvo(
      form.includeTranslationForm,
      false
    )
    form.translationFormLanguage = editIlmoitettuArvo(
      form.translationFormLanguage,
      null
    )
  }
}

function setIncludeTranslationForm(form: Form, value: CheckboxValue) {
  form.includeTranslationForm = editIlmoitettuArvo(
    form.includeTranslationForm,
    value.checkboxState
  )

  if (finalEditableFieldValue(form.includeTranslationForm) !== true) {
    form.translationFormLanguage = editIlmoitettuArvo(
      form.translationFormLanguage,
      null
    )
  }
}

function setTranslationFormLanguage(
  form: Form,
  value: SingleSelectValue<string>
) {
  form.translationFormLanguage = editIlmoitettuArvo(
    form.translationFormLanguage,
    isValueInEnum(value?.uniqueItemId, z.enum(availableTranslations).Enum)
      ? value.uniqueItemId
      : null
  )
}

function setIncludeApostille(form: Form, value: CheckboxValue) {
  form.includeApostille = editIlmoitettuArvo(
    form.includeApostille,
    value.checkboxState
  )
}

function setContactPhone(form: Form, value: TextInputValue) {
  form.spouseA.contactInfo.phoneNumber = editIlmoitettuArvo(
    form.spouseA.contactInfo.phoneNumber,
    !value ? '' : String(value)
  )
}

function setContactEmail(form: Form, value: TextInputValue) {
  form.spouseA.contactInfo.email = editIlmoitettuArvo(
    form.spouseA.contactInfo.email,
    !value ? '' : String(value)
  )
}

function getMailDeliveryFieldSetter(
  propertyName: (typeof mailDeliveryFields)[number]
) {
  return (form: Form, value: TextInputValue) => {
    form.mailDelivery[propertyName] = editIlmoitettuArvo(
      form.mailDelivery[propertyName],
      value?.toString() ?? null
    )
  }
}
